import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

import * as Objects from '@core/objects';
import * as fromFileSignaturesState from '../../store/state/file-signatures/file-signatures.state';
import {
    CacheFileSignatures,
    RefreshFileSignatures,
    ClearFileSignatures
} from '../../store/state/file-signatures/file-signatures.actions';

/**
 * CoreFileSignatureService
 * 
 * Centralized service for managing file signatures across the Core layer.
 * Works independently of the App layer, allowing reuse across different applications (App, Login, Share).
 * 
 * Responsibilities:
 * 1. Store current versionId context (user's active version)
 * 2. Cache signatures from loaded messages
 * 3. Refresh signatures when expired
 * 4. Broadcast signature updates to all components
 * 
 * Usage:
 * ```
 * // After loading version comments in App
 * this.coreFileSignatureService.setVersionId(versionId);
 * this.coreFileSignatureService.updateSignaturesFromMessages(messages);
 * 
 * // When user performs action with expired signature
 * this.coreFileSignatureService.refreshSignatures();
 * 
 * // In components, subscribe to signature updates
 * this.coreFileSignatureService.getSignatureUpdates$().subscribe(...)
 * 
 * // When user leaves version
 * this.coreFileSignatureService.clearVersionContext();
 * ```
 */
@Injectable({
    providedIn: 'root'
})
export class CoreFileSignatureService {
    /**
     * Current version ID context
     * Only one version can be open at a time
     * Cleared when user leaves the version
     */
    private currentVersionId: number | null = null;

    /**
     * Debounced refresh queue for batching multiple refresh requests
     * Multiple rapid refresh requests are consolidated into a single refresh
     */
    private refreshQueue$ = new Subject<void>();

    /**
     * Broadcasts signature updates to all listening components
     * Format: { fileId: string, access: AmsFileAccess }
     */
    private signatureBroadcast$ = new Subject<{ fileId: string; access: any }>();

    /**
     * Cleanup subject for subscriptions
     */
    private destroy$ = new Subject<void>();

    constructor(private store: Store<{ fileSignatures: fromFileSignaturesState.FileSignatures }>) {
        this.initializeRefreshQueue();
    }

    /**
     * Set the current version ID context
     * Call this when a version is opened/loaded
     * 
     * @param versionId The ID of the currently active version
     */
    public setVersionId(versionId: number): void {
        this.currentVersionId = versionId;
    }

    /**
     * Clear the version context
     * Call this when user leaves the version to prevent stale refreshes
     */
    public clearVersionContext(): void {
        this.currentVersionId = null;
        this.store.dispatch(new ClearFileSignatures());
    }

    /**
     * Update signatures cache from loaded messages
     * Called by App version effects after loading comments
     * Extracts all file signatures and caches them in Core store
     * 
     * @param messages Array of VersionMessage objects with attached files
     */
    public updateSignaturesFromMessages(messages: Objects.VersionMessage[]): void {
        if (!messages || messages.length === 0) {
            return;
        }

        const signatures: { [fileId: string]: any } = {};

        // Extract signatures from all messages and their files
        messages.forEach((versionMessage: Objects.VersionMessage) => {
            const message = versionMessage.message;
            if (message) {
                // Check attachments (AmsFile[] with signatures)
                if (message.attachments && Array.isArray(message.attachments)) {
                    message.attachments.forEach((file: Objects.AmsFile) => {
                        if (file.id && file.access) {
                            signatures[file.id] = file.access;
                            // Broadcast individual update so all components get fresh cache
                            this.signatureBroadcast$.next({ fileId: file.id, access: file.access });
                        }
                    });
                }
            }
        });

        // Dispatch to store to cache all signatures
        if (Object.keys(signatures).length > 0) {
            this.store.dispatch(new CacheFileSignatures(signatures));
        }
    }

    /**
     * Refresh file signatures by fetching latest from API
     * Call this when a file signature has expired and action was blocked
     * 
     * Uses the currently stored versionId to fetch fresh signatures
     */
    public refreshSignatures(): void {
        if (!this.currentVersionId) {
            return;
        }

        this.refreshQueue$.next();
    }

    /**
     * Observable stream of signature updates
     * All file list components subscribe to this to stay in sync
     * 
     * @returns Observable of { fileId, access } updates
     */
    public getSignatureUpdates$(): Observable<{ fileId: string; access: any }> {
        return this.signatureBroadcast$.asObservable();
    }

    /**
     * Get current version ID context
     * 
     * @returns Current version ID or null if not set
     */
    public getVersionId(): number | null {
        return this.currentVersionId;
    }

    /**
     * Check if a version is currently active
     * 
     * @returns true if a version is set, false otherwise
     */
    public hasActiveVersion(): boolean {
        return this.currentVersionId !== null;
    }

    /**
     * Initialize debounced refresh queue
     * Consolidates multiple rapid refresh requests into a single refresh
     */
    private initializeRefreshQueue(): void {
        this.refreshQueue$
            .pipe(
                debounceTime(100), // Wait for 100ms of silence
                distinctUntilChanged(),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                if (this.currentVersionId) {
                    this.store.dispatch(new RefreshFileSignatures(this.currentVersionId));
                }
            });
    }

    /**
     * Cleanup resources
     */
    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.refreshQueue$.complete();
        this.signatureBroadcast$.complete();
    }
}
